import React, { Component } from "react";
import { Route, Redirect, Switch } from "react-router-dom";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { parse, stringify } from "query-string";
import ForgotPasswordPageWrapper from "./pages/forgot_password/forgot_password_page_wrapper";
import ForgotPasswordSent from "./pages/forgot_password/forgot_password_sent";
import HomeRedirectWrapper from "./base/home_redirect/home_redirect_wrapper";
import MessageBar from "./base/message_bar";
import Sidebar from "./base/sidebar";
import SidebarMenuButton from "./base/sidebar/sidebar_menu_button";
import WarningBarWrapper from "./base/warning_bar/warning_bar_wrapper";
import ModalRoot, {
  IMPORT_MODAL,
  SHOW_FORM_MODAL,
  EPISODE_FORM_MODAL,
  SPONSORSHIP_MODAL,
  SHOW_REDIRECT_HELP_MODAL,
  AUDIO_BLOCK_CREATE_MODAL,
  AUDIO_BLOCK_EDIT_MODAL,
  SET_EPISODE_MARKERS_MODAL,
  CAMPAIGN_EDITOR_MODAL,
  CAMPAIGN_PODCAST_SELECTOR,
  CAMPAIGN_PODCAST_SCHEDULE,
  CAMPAIGN_SUMMARY_VIEW,
} from "./modals/modal_root";
import AccountPageWrapper from "./pages/account/account_page";
import AdminSignInAsPageWrapper from "./pages/admin/admin_sign_in_as_page_wrapper";
import AdvertiserWelcomeModal from "./pages/campaigns/advertiser_welcome_modal";
import EmailVerificationPageWrapper from "./pages/email_verification/email_verification_page_wrapper";
import EpisodePageWrapper from "./pages/episodes/episode_page_wrapper";
import MonetizationPageWrapper from "./pages/monetization/monetization_page_wrapper";
import OAuthRedirectPageWrapper from "./pages/monetization/oauth_redirect_page_wrapper";
import RecoverPasswordPageWrapper from "./pages/forgot_password/recover_password_page_wrapper";
import SignInPageWrapper from "./pages/sign_in/sign_in_page_wrapper";
import SignUpWrapper from "./pages/sign_up/sign_up_page_wrapper";
import StatsPageWrapper from "./pages/stats/stats_page_wrapper";
import "redcircle-brand-css/scss/redcircle.scss";
import { nLevelsUp, oneLevelUp } from "./lib/routing";
import { classNames } from "react-extras";
import DealPageWrapper from "./pages/deals/deal_page_wrapper";
import PromotionsDashboardWrapper from "./pages/promotions/promotions_dashboard_wrapper";
import BrowsePromotionPage from "./pages/browse_promotion/browse_promotion_page";
import PromotionCategoryPage from "./pages/promotions/promotion_category";
import SponsorSignUpPage from "./pages/sign_up/sponsor_sign_up";
import SponsorShowList from "./pages/sponsor/sponsor_show_list";
import SponsorTransaction from "./pages/sponsor_transactions/sponsor_transaction_page";
import SponsorShowPage from "./pages/sponsor/sponsor_show_page";
import ShowExclusiveContentPage from "./pages/subscriptions/show_exclusive_content";
import ShowDonationsPage from "./pages/subscriptions/show_monthly_donations";
import YoutubeDummyPage from "./pages/youtube/youtube_dummy_page";
import ShowEpisodesPage from "./pages/shows/show_episodes_page";
import ShowDistributionPage from "./pages/shows/show_distribution_page";
import AudioBlocksPage from "./pages/audio_blocks/audio_blocks_page";
import { getGoogleAnalyticsID } from "../lib/config";

import "./app.scss";
import "./lib/info/tooltip.scss";

import ModalLauncher from "./modals/modal_launcher";
import { If } from "react-extras";
import knownAgentFriendly from "../lib/known_agent_friendly";
import { sendBootToIntercom, resetIntercom } from "../lib/intercom";
import SupportPage from "./pages/support/support_page";
import UserStore from "../lib/user_store";
import { Helmet } from "react-helmet";
import { isMobile } from "../constants/css";
import PromotionsShowPage from "./pages/promotions/promotions_show_details";
import PromotionsDirectoryShowPage from "./pages/promotions/promotions_directory_show_details";
import TipPage from "./pages/tip/tip_page";
import TipSuccess from "./pages/tip/tip_success";
import { userRoleExists } from "../lib/user";
import MarkersPage from "./pages/audio_management/markers";
import AudioManagementPage from "./pages/audio_management/audio_management";
import ShowMarkersPage from "./pages/shows/show_markers_page";
import EpisodeEmbedPage from "./pages/episodes/episode_embed";
import ShowAdvertisingPage from "./pages/shows/show_advertising_page";
import ShowListPageWrapper from "./pages/shows/show_list_page_wrapper";
import ShowDetailsPageWrapper from "./pages/shows/show_details_page_wrapper";
import CampaignListPage from "./pages/campaigns/campaign_list_page";
import CampaignPage from "./pages/campaigns/campaign_page";
import AdvertiserSignUpPage from "./pages/sign_up/advertiser_sign_up";
import ShowCampaignPage from "./pages/shows/show_campaign_page";
import AdvertiserBrowsePage from "./pages/advertiser_browse/browse_podcasts";
import AdvertiserBrowseShowPage from "./pages/advertiser_browse/advertiser_browse_show_page";

// keeps track of logged-in user
export const UserContext = React.createContext({});

class App extends Component {
  dataSentToIntercom = false;

  handleIntercom = props => {
    if (get(props, "user.uuid")) {
      if (!this.dataSentToIntercom) {
        this.dataSentToIntercom = true;
        if (!UserStore.isAdminLogin()) {
          sendBootToIntercom(props.user);
        }
      }
    } else if (this.dataSentToIntercom) {
      resetIntercom();
      this.dataSentToIntercom = false;
    }
  };

  componentDidMount() {
    // Close the sidebar for small width screens at boot.  Handle re-sizes as well.
    if (isMobile()) {
      this.props.toggleSidebar();
    }
    window.addEventListener("resize", () => {
      if (!isMobile() && !this.props.sidebarOpen) {
        this.props.toggleSidebar();
      }
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.handleIntercom(nextProps);
  }

  UNSAFE_componentWillUpdate({ location, history }) {
    const gtag = window.gtag;

    if (location.pathname === this.props.location.pathname) {
      return;
    }

    if ((history.action === "PUSH" || history.action === "REPLACE") && typeof gtag === "function") {
      gtag("config", getGoogleAnalyticsID(), {
        page_location: window.location.href,
        page_path: location.pathname,
      });
    }
  }

  getRoleExists = () => this.props.user && userRoleExists(this.props.user);

  UNSAFE_componentWillMount() {
    this.handleIntercom(this.props);
    this.props.fetchStatOptions().then(resp => {
      if (!get(resp, "json")) return;
      knownAgentFriendly.set(resp.json);
    });
  }

  render() {
    const roleExists = this.getRoleExists();
    // If a user is not signed in but is trying to access a loggedInContent page
    // we ensure they arrive to that page after /sign-in
    const urlParams = parse(window.location.search);
    const { goto, ...rest } = urlParams;
    if (roleExists && !isEmpty(goto)) {
      return (
        <Redirect
          push
          to={{
            pathname: goto,
            search: stringify(rest),
          }}
        />
      );
    }
    var mainContentClasses = "main-content";
    var hideSidebar = false;

    if (!roleExists) {
      hideSidebar = true;
    }
    if (!this.props.sidebarOpen || hideSidebar) {
      mainContentClasses += " main-content--no-sidebar";
    }
    let currentLocation = "";
    if (!roleExists) {
      currentLocation = encodeURIComponent(window.location.pathname);
    }
    const loggedOutContent = (
      <Switch>
        <Route
          path="/advertiser-welcome/:email/:advertiserContactToken"
          render={props => [
            <CampaignListPage key={"campaign-page"} />,
            <AdvertiserWelcomeModal key={"ad-welcome-modal"} />,
          ]}
        />
        <Route path="/admin/sign-in-as" component={AdminSignInAsPageWrapper} />
        <Route
          path="/sign-in/creator"
          render={props => <SignInPageWrapper {...props} forRole="creator" />}
        />
        <Route
          path="/sign-in/sponsor"
          render={props => <SignInPageWrapper {...props} forRole="sponsor" />}
        />
        <Route path="/sign-in" component={SignInPageWrapper} />
        <Route path="/forgot-password" component={ForgotPasswordPageWrapper} />
        <Route path="/forgot-password-sent" component={ForgotPasswordSent} />
        <Route
          path="/recover-password/:email/:passwordToken"
          component={RecoverPasswordPageWrapper}
        />
        <Route path="/sign-up/advertiser" component={AdvertiserSignUpPage} />
        <Route
          path="/sign-up/sponsor"
          render={props => <SignUpWrapper {...props} forRole={"sponsor"} />}
        />
        <Route
          path="/sign-up/creator"
          render={props => <SignUpWrapper {...props} forRole={"creator"} />}
        />
        <Route path="/sign-up" component={SignUpWrapper} />
        <Route path="/shows/:showUUID/tip" component={TipPage} />
        <Route path="/shows/:showUUID/exclusive-content" exact component={SponsorShowPage} />
        <Route path="/shows/:showUUID/donations" exact component={SponsorShowPage} />
        <Route path="/shows/:showUUID/tip-success" component={TipSuccess} />
        <Route
          path="/shows/:showUUID/exclusive-content/edit"
          render={props => [
            <SponsorShowPage key={"sponsor-page"} />,
            <ModalLauncher
              key={"modal-launcher"}
              launchModal={this.props.showModal}
              modalType={SPONSORSHIP_MODAL}
              closeModal={this.props.hideModal}
              closeRoute={oneLevelUp}
              {...props}
            />,
          ]}
        />
        <Route
          path="/shows/:showUUID/donations/edit"
          render={props => [
            <SponsorShowPage key={"sponsor-page"} />,
            <ModalLauncher
              key={"modal-launcher"}
              launchModal={this.props.showModal}
              modalType={SPONSORSHIP_MODAL}
              closeModal={this.props.hideModal}
              closeRoute={oneLevelUp}
              {...props}
            />,
          ]}
        />
        <Route
          path="/shows/:showUUID/sponsor/edit"
          render={props => [
            <SponsorShowPage key={"sponsor-page"} />,
            <ModalLauncher
              key={"modal-launcher"}
              launchModal={this.props.showModal}
              modalType={SPONSORSHIP_MODAL}
              closeModal={this.props.hideModal}
              closeRoute={oneLevelUp}
              {...props}
            />,
          ]}
        />
        <Route path="/shows/:showUUID/sponsor" component={SponsorShowPage} />
        <Route
          path="/shows/:showUUID/resubscribe"
          render={props => <SponsorShowPage {...props} resubscribe={true} />}
        />
        {!roleExists && <Redirect to={`/sign-in?goto=${currentLocation}`} />}
      </Switch>
    );

    const loggedInContent = (
      <React.Fragment>
        <Route path="/support" component={SupportPage} />
        <Route path="/oauth-redirect" component={OAuthRedirectPageWrapper} />
        <Route path="/monetization" component={MonetizationPageWrapper} />
        <Route path="/sponsor-transactions" component={SponsorTransaction} />
        <Route path="/sponsor-show-list" component={SponsorShowList} />
        <Route path="/subscriptions/:subscriptionUUID" component={SponsorShowPage} />
        <Route path="/start-a-podcast" component={SponsorSignUpPage} />
        <Route path="/account" component={AccountPageWrapper} />
        <Route path="/verify-email/:verificationToken" component={EmailVerificationPageWrapper} />
        <Route path="/youtube-oauth" component={YoutubeDummyPage} />
        <Switch>
          <Route
            path="/insertion-points/episodes/:episodeUUID"
            render={props => [
              <MarkersPage key="markers" />,
              <ModalLauncher
                key="modal-launcher"
                {...props}
                launchModal={this.props.showModal}
                modalType={SET_EPISODE_MARKERS_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={nLevelsUp(2)}
              />,
            ]}
          />
          <Route path="/insertion-points" component={MarkersPage} />

          <Route path="/dynamic-insertion" component={AudioManagementPage} />
        </Switch>
        <Switch>
          <Route
            path="/campaigns/new"
            exact
            render={() => [
              <CampaignListPage key={"campaign-list-page"} />,
              <ModalLauncher
                isEdit={false}
                key={"campaign-page-modal"}
                launchModal={this.props.showModal}
                modalType={CAMPAIGN_EDITOR_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={oneLevelUp}
              />,
            ]}
          />
          <Route
            path="/campaigns/:campaignUUID/select"
            exact
            render={props => [
              <CampaignPage key={"campaign-page"} />,
              <ModalLauncher
                key={"podcast-selector"}
                launchModal={this.props.showModal}
                modalType={CAMPAIGN_PODCAST_SELECTOR}
                closeModal={this.props.hideModal}
                closeRoute={oneLevelUp}
              />,
            ]}
          />
          <Route
            path="/campaigns/:campaignUUID/schedule"
            exact
            render={props => [
              <CampaignPage key={"campaign-page"} />,
              <ModalLauncher
                key={"podcast-scheduler"}
                launchModal={this.props.showModal}
                modalType={CAMPAIGN_PODCAST_SCHEDULE}
                closeModal={this.props.hideModal}
                closeRoute={oneLevelUp}
                {...props}
              />,
            ]}
          />
          <Route path="/browse" exact component={AdvertiserBrowsePage} />
          <Route path="/browse/:showUUID" exact component={AdvertiserBrowseShowPage} />
          <Route path="/campaigns/:campaignUUID" exact component={CampaignPage} />

          <Route
            path="/campaigns/:campaignUUID/view"
            exact
            render={props => [
              <CampaignPage key={"campaign-page"} />,
              <ModalLauncher
                isEdit={true}
                key={"campaign-page-modal"}
                launchModal={this.props.showModal}
                modalType={CAMPAIGN_SUMMARY_VIEW}
                closeModal={this.props.hideModal}
                closeRoute={oneLevelUp}
                {...props}
              />,
            ]}
          />
          <Route
            path="/campaigns/:campaignUUID/edit"
            exact
            render={props => [
              <CampaignListPage key={"campaign-page"} />,
              <ModalLauncher
                isEdit={true}
                key={"campaign-page-modal"}
                launchModal={this.props.showModal}
                modalType={CAMPAIGN_EDITOR_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={oneLevelUp}
                {...props}
              />,
            ]}
          />
          {/*this is a hack because we open this from two places. background should stay the same*/}
          <Route
            path="/campaigns/:campaignUUID/editing"
            exact
            render={props => [
              <CampaignPage key={"campaign-page"} />,
              <ModalLauncher
                isEdit={true}
                key={"campaign-page-modal"}
                launchModal={this.props.showModal}
                modalType={CAMPAIGN_EDITOR_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={oneLevelUp}
                {...props}
              />,
            ]}
          />
          <Route path="/campaigns" component={CampaignListPage} />
        </Switch>

        <Route path="/stats" component={StatsPageWrapper} />
        <Switch>
          <Route path="/promotions/categories/:categoryUUID" component={PromotionCategoryPage} />
          <Route path="/promotions/shows/:showUUID" exact component={PromotionsShowPage} />
          <Route
            path="/promotions/directory-shows/:directoryShowUUID"
            exact
            component={PromotionsDirectoryShowPage}
          />
          <Route path="/promotions/browse" exact component={BrowsePromotionPage} />
          <Route path="/promotions/deals/:dealUUID" component={DealPageWrapper} />
          <Route path="/promotions" exact component={PromotionsDashboardWrapper} />
        </Switch>

        <Switch>
          <Route path="/audio-blocks" exact component={AudioBlocksPage} />
          <Route
            path="/audio-blocks/create"
            render={props => [
              <AudioBlocksPage key={"audio-block"} />,
              <ModalLauncher
                key={"modal-launcher"}
                {...props}
                launchModal={this.props.showModal}
                modalType={AUDIO_BLOCK_CREATE_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={oneLevelUp}
              />,
            ]}
          />
          <Route
            exact
            path="/audio-blocks/:audioBlockUUID"
            render={props => [
              <AudioBlocksPage key={"audio-block"} />,
              <ModalLauncher
                key={"modal-launcher"}
                {...props}
                startingPage={1}
                disableNav
                launchModal={this.props.showModal}
                modalType={AUDIO_BLOCK_EDIT_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={nLevelsUp(1)}
              />,
            ]}
          />
          <Route
            path="/audio-blocks/:audioBlockUUID/edit"
            render={props => [
              <AudioBlocksPage key={"audio-block"} />,
              <ModalLauncher
                key={"modal-launcher"}
                {...props}
                launchModal={this.props.showModal}
                modalType={AUDIO_BLOCK_EDIT_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={nLevelsUp(2)}
              />,
            ]}
          />
          <Route
            path="/audio-blocks/:audioBlockUUID/assign"
            render={props => [
              <AudioBlocksPage key={"audio-block"} />,
              <ModalLauncher
                key={"modal-launcher"}
                {...props}
                launchModal={this.props.showModal}
                modalType={AUDIO_BLOCK_EDIT_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={nLevelsUp(2)}
                startingPage={2}
              />,
            ]}
          />
        </Switch>
        <Switch>
          <Route path="/shows/:showUUID/advertising" component={ShowAdvertisingPage} />
          <Route path="/shows/:showUUID/campaigns/:campaignUUID" component={ShowCampaignPage} />
          <Route
            path="/shows/:showUUID/ep/:episodeUUID/insertion-points"
            render={props => [
              <EpisodePageWrapper key="episode-page-wrapper" />,
              <ModalLauncher
                key="modal-launcher"
                {...props}
                launchModal={this.props.showModal}
                modalType={SET_EPISODE_MARKERS_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={oneLevelUp}
              />,
            ]}
          />
          <Route
            path="/shows/:showUUID/ep/:episodeUUID/edit"
            render={props => [
              <EpisodePageWrapper key={"episode-page-wrapper"} />,
              <ModalLauncher
                key={"episode-page-modal"}
                {...props}
                launchModal={this.props.showModal}
                modalType={EPISODE_FORM_MODAL}
                closeModal={this.props.hideModal}
                mode="edit"
                closeRoute={oneLevelUp}
              />,
            ]}
          />

          <Route path="/shows/:showUUID/insertion-points" component={ShowMarkersPage} />
          <Route path="/shows/:showUUID/i/exclusive-content" component={ShowExclusiveContentPage} />
          <Route path="/shows/:showUUID/i/donations" component={ShowDonationsPage} />
          <Route path="/shows/:showUUID/ep" exact component={ShowEpisodesPage} />
          <Route path="/shows/:showUUID/distribution" component={ShowDistributionPage} />
          <Route
            path="/shows/:showUUID/sponsor/create"
            render={props => [
              <SponsorShowPage key={"sponsor-show-page"} />,
              <ModalLauncher
                key={"sponsor-show-page-modal"}
                {...props}
                launchModal={this.props.showModal}
                modalType={SPONSORSHIP_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={oneLevelUp}
              />,
            ]}
          />

          <Route
            path="/shows/:showUUID/ep/create"
            render={props => [
              <ShowEpisodesPage key="details-wrapper" />,
              <ModalLauncher
                key="details-wrapper-modal"
                {...props}
                launchModal={this.props.showModal}
                modalType={EPISODE_FORM_MODAL}
                closeModal={this.props.hideModal}
                mode="create"
                closeRoute={oneLevelUp}
              />,
            ]}
          />
          <Route path="/shows/:showUUID/ep/:episodeUUID/embed" component={EpisodeEmbedPage} />
          <Route path="/shows/:showUUID/ep/:episodeUUID" exact component={EpisodePageWrapper} />
          <Route
            path="/shows/:showUUID/edit"
            render={props => [
              <ShowEpisodesPage key="details-wrapper" />,
              <ModalLauncher
                key="details-wrapper-modal"
                {...props}
                launchModal={this.props.showModal}
                modalType={SHOW_FORM_MODAL}
                closeModal={this.props.hideModal}
                mode="edit"
                closeRoute={oneLevelUp}
              />,
            ]}
          />
          <Route
            path="/shows/:showUUID/redirect-help"
            render={props => [
              <ShowDetailsPageWrapper key={"details-wrapper"} />,
              <ModalLauncher
                key={"redirect-help-modal"}
                {...props}
                launchModal={this.props.showModal}
                modalType={SHOW_REDIRECT_HELP_MODAL}
                closeModal={this.props.hideModal}
                mode="edit"
                closeRoute={oneLevelUp}
              />,
            ]}
          />
          <Route
            path="/shows/create"
            render={props => [
              <ShowListPageWrapper key={"wrapper"} />,
              <ModalLauncher
                key={"show-modal"}
                {...props}
                mode="create"
                launchModal={this.props.showModal}
                modalType={SHOW_FORM_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={() => "/shows"}
              />,
            ]}
          />
          <Route
            path="/shows/import"
            render={props => [
              <ShowListPageWrapper key={"wrapper"} />,
              <ModalLauncher
                key={"import-modal"}
                {...props}
                launchModal={this.props.showModal}
                modalType={IMPORT_MODAL}
                closeModal={this.props.hideModal}
                closeRoute={() => "/shows"}
              />,
            ]}
          />
          <Route
            path="/shows/:showUUID"
            exact={true}
            render={props => <ShowDetailsPageWrapper {...props} key="details-wrapper" />}
          />
          <Route
            path="/shows"
            exact={true}
            render={props => <ShowListPageWrapper {...props} key={"wrapper"} />}
          />
        </Switch>

        <Route exact path="/" component={HomeRedirectWrapper} />
        {loggedOutContent}
      </React.Fragment>
    );

    const content = roleExists ? loggedInContent : loggedOutContent;

    return (
      <UserContext.Provider value={this.props.user}>
        <div className={classNames("main-wrapper", roleExists && "main-wrapper--logged-in")}>
          <Helmet>
            <title>RedCircle</title>
          </Helmet>
          <MessageBar
            message={this.props.message}
            messageUUID={this.props.messageUUID}
            messageType={this.props.messageType}
            dismissMessageBar={this.props.dismissMessageBar}
          />
          <Sidebar
            user={this.props.user}
            toggleSidebar={this.props.toggleSidebar}
            sidebarOpen={this.props.sidebarOpen}
            hideSidebar={hideSidebar}
            push={this.props.history.push}
          />

          <ModalRoot />
          <div className={mainContentClasses}>
            <WarningBarWrapper user={this.props.user} />
            <If condition={Boolean(this.props.user)}>
              <div className="mobile-top-bar">
                <SidebarMenuButton onClick={this.props.toggleSidebar} />
              </div>
            </If>
            {content}
          </div>
        </div>
      </UserContext.Provider>
    );
  }
}

export default App;
